import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReactECharts from "echarts-for-react";
import _ from "lodash";

// Gráfico de línea para una variable. Toma del dashboard lo que ya estaba resuelto ahí —paleta, tooltip en 24 h,
// unidad alineada al eje— pero sin su acoplamiento a los widgets: acá solo entra una serie de puntos.
const SERIES_COLOR = "#5C149B";

const pad = (value) => `${value}`.padStart(2, "0");

const formatTooltipDateTime = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return `${value ?? ""}`;
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatValue = (value, decimals) => {
    if (!_.isFinite(Number(value))) return `${value ?? "-"}`;
    return Number(value).toLocaleString("es-CL", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const TimeSeriesChart = ({ data = [], unit = "", decimals = 1, height = 180, color = SERIES_COLOR }) => {
    const option = useMemo(() => ({
        color: [color],
        animation: false,
        grid: { top: 26, right: 12, bottom: 24, left: 46 },
        tooltip: {
            trigger: "axis",
            backgroundColor: "rgba(15, 23, 42, 0.94)",
            borderWidth: 0,
            textStyle: { color: "#F8FAFC", fontSize: 12 },
            extraCssText: "box-shadow: 0 10px 28px rgba(15, 23, 42, 0.28); border-radius: 10px; padding: 8px 10px;",
            formatter: (params) => {
                const entry = Array.isArray(params) ? params[0] : params;
                const value = Array.isArray(entry?.value) ? entry.value[1] : entry?.value;
                return `<div style="font-size:11px;color:#CBD5E1">${formatTooltipDateTime(entry?.axisValue)}</div>`
                    + `<b style="color:#F8FAFC">${formatValue(value, decimals)}${unit ? ` ${unit}` : ""}</b>`;
            },
        },
        xAxis: {
            type: "time",
            axisLabel: { hideOverlap: true, color: "#64748B", fontSize: 10 },
            axisLine: { lineStyle: { color: alpha("#334155", 0.45) } },
            splitLine: { show: false },
        },
        yAxis: {
            type: "value",
            name: unit || undefined,
            // Igual que en el dashboard: anclada al extremo del eje y alineada a la derecha, sobre sus etiquetas.
            nameLocation: "end",
            nameGap: 8,
            nameTextStyle: { color: "#64748B", fontSize: 10, fontWeight: 600, align: "right", verticalAlign: "bottom" },
            axisLabel: { color: "#64748B", fontSize: 10, formatter: (value) => formatValue(value, decimals) },
            splitLine: { lineStyle: { color: alpha("#94A3B8", 0.24), type: "dashed" } },
        },
        series: [{
            type: "line",
            smooth: true,
            showSymbol: false,
            lineStyle: { width: 2 },
            areaStyle: { color: alpha(color, 0.12) },
            data: data.map((point) => [point.x, point.y]),
        }],
    }), [color, data, decimals, unit]);

    if (!data.length) {
        return (
            <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="caption" color="text.secondary">Sin datos en este rango</Typography>
            </Box>
        );
    }

    return (
        <ReactECharts
            option={option}
            notMerge={false}
            replaceMerge={["series"]}
            lazyUpdate
            style={{ height, width: "100%" }}
        />
    );
};

export default TimeSeriesChart;
